/** Native module-offer transport; backend owns terms, approval and authority. */
import { httpClient } from "./client";
import { canonicalModuleOfferLocator, readAccepted, readCommitment, readCommitmentPage, readOffer, readOfferPage, requireAcceptance } from "./moduleOfferResponse";
import type {
  CommercialAcceptRequest,
  CommercialCommitmentOwnerPage,
  CommercialCommitmentOwnerView,
  CommercialOfferDraft,
  CommercialOfferDraftPage,
  CommercialOfferDraftReplace,
  CommercialOfferDraftWrite,
  CommercialOfferOwnerPage,
  CommercialOfferOwnerView,
  CommercialOfferPublicationReview,
  CommercialPublicationRequest,
  CommercialWorkspaceReadiness,
} from "../types/generated";

export interface ModuleOfferPageParams {
  limit?: number;
  offset?: number;
}

export interface ModuleOfferAuthoringApi extends ModuleOfferVersionedPublicationApi {
  listDrafts(params?: ModuleOfferPageParams): Promise<CommercialOfferDraftPage>;
  createDraft(data: CommercialOfferDraftWrite): Promise<CommercialOfferDraft>;
  getDraft(draftId: string): Promise<CommercialOfferDraft>;
  replaceDraft(
    draftId: string,
    data: CommercialOfferDraftReplace,
  ): Promise<CommercialOfferDraft>;
  getPublicationReview(
    draftId: string,
    environment: CommercialPublicationRequest["environment"],
  ): Promise<CommercialOfferPublicationReview>;
}

export interface OwnerModuleOfferApi {
  listOffers(
    orgId: string,
    params?: ModuleOfferPageParams,
  ): Promise<CommercialOfferOwnerPage>;
  getOffer(orgId: string, versionId: string): Promise<CommercialOfferOwnerView>;
  accept(
    orgId: string,
    data: CommercialAcceptRequest,
  ): Promise<CommercialCommitmentOwnerView>;
  listCommitments(
    orgId: string,
    params?: ModuleOfferPageParams,
  ): Promise<CommercialCommitmentOwnerPage>;
  getCommitment(
    orgId: string,
    acceptanceId: string,
  ): Promise<CommercialCommitmentOwnerView>;
  getReadiness(orgId: string): Promise<CommercialWorkspaceReadiness>;
}

export const moduleOfferAuthoringApi: ModuleOfferAuthoringApi = {
  listDrafts: (params) =>
    httpClient.get(`/platform/commercial/offer-drafts${pageQuery(params)}`),
  createDraft: (data) =>
    httpClient.post("/platform/commercial/offer-drafts", data),
  getDraft: (id) =>
    httpClient.get(
      `/platform/commercial/offer-drafts/${encodeURIComponent(id)}`,
    ),
  replaceDraft: (id, data) =>
    httpClient.put(
      `/platform/commercial/offer-drafts/${encodeURIComponent(id)}`,
      data,
    ),
  getPublicationReview: (id, environment) =>
    httpClient.get(
      `/platform/commercial/offer-drafts/${encodeURIComponent(id)}/publication-review?environment=${encodeURIComponent(environment)}`,
    ),
  publish: (id, data) =>
    httpClient.post(
      `/platform/commercial/offer-drafts/${encodeURIComponent(id)}/publish`,
      data,
    ),
  getVersion: (id) =>
    httpClient.get(
      `/platform/commercial/offers/versions/${encodeURIComponent(id)}`,
    ),
};

export const ownerModuleOfferApi: OwnerModuleOfferApi = {
  listOffers: async (org, params) => {
    const expected = { limit: params?.limit === undefined ? 50 : params.limit, offset: params?.offset === undefined ? 0 : params.offset };
    const path = `/orgs/${locator(org)}/commercial/offers${pageQuery(params)}`;
    return readOfferPage(await httpClient.get<unknown>(path), path, org, expected);
  },
  getOffer: async (org, version) => {
    const path = `/orgs/${locator(org)}/commercial/offers/${locator(version)}`;
    return readOffer(await httpClient.get<unknown>(path), path, org, version);
  },
  accept: async (org, data) => {
    requireAcceptance(data);
    const exact = Object.freeze({ ...data });
    const path = `/orgs/${locator(org)}/commercial/acceptances`;
    return readAccepted(await httpClient.post<unknown>(path, exact), path, org, exact);
  },
  listCommitments: async (org, params) => {
    const expected = { limit: params?.limit === undefined ? 50 : params.limit, offset: params?.offset === undefined ? 0 : params.offset };
    const path = `/orgs/${locator(org)}/commercial/acceptances${pageQuery(params)}`;
    return readCommitmentPage(await httpClient.get<unknown>(path), path, org, expected);
  },
  getCommitment: async (org, id) => {
    const path = `/orgs/${locator(org)}/commercial/acceptances/${locator(id)}`;
    return readCommitment(await httpClient.get<unknown>(path), path, org, id);
  },
  getReadiness: (org) =>
    httpClient.get(`/orgs/${locator(org)}/commercial/readiness`),
};

function locator(value: string): string {
  const canonical = canonicalModuleOfferLocator(value);
  if (canonical === null) throw new TypeError("A valid module-offer locator is required.");
  return encodeURIComponent(canonical);
}

function pageQuery(params: ModuleOfferPageParams = {}): string {
  const query = new URLSearchParams();
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  if (params.offset !== undefined) query.set("offset", String(params.offset));
  return query.size ? `?${query}` : "";
}

/** Exact versioned result contract for the SAME two endpoints.
 * New draft/publication writes stay V2-only. Genuine historical publication
 * replay and immutable GET retain the explicit stored-format V1|V2 read union.
 * The installed authoring interface composes these exact signatures;
 * no fallback parser, broad untagged alias, second API or V1 writer is added.
 */
export interface ModuleOfferVersionedPublicationApi {
  publish(draftId: string, data: CommercialPublicationRequest): Promise<CommercialOfferOwnerView>;
  getVersion(versionId: string): Promise<CommercialOfferOwnerView>;
}
